// src/app/services/performance-series/fixtures.ts
//
// SYNTHETIC Performance series data, shaped EXACTLY like the wire envelope in
// PERFORMANCE_SERIES_ENDPOINT_SPEC.md. The fixture path feeds these through the
// same validateEnvelope() the real endpoint output will use, so building the UI
// against the fixture is a faithful rehearsal of the live contract.
//
// The numbers are illustrative, not measured. They exist so the module can be
// explored locally before the backend ships. Dates are fixed (not relative) so
// every render is deterministic and reproducible.
//
// Coverage built in on purpose:
//   - Iterate  : multi-run (baseline + Day 11), every layer populated, a research
//                variable that only appears in the later run (alignment test),
//                gptweb present as its own member but kept OUT of overall.
//   - Sunbit   : AI Index trends over several monitoring runs, but Deep Signal has
//                ONE run -> funnel/variable lenses are "baseline only"; competitive
//                arrives empty -> the layer must degrade out cleanly.
//   - Northwind: a fully-populated multi-run brand with a complete competitive
//                layer, to show the system scaling to a richer dataset.
//
// No React, no `@/` imports — headless-testable.

import type { RawEnvelope, RawSeriesPoint } from "./types";

// ── Tiny builders ────────────────────────────────────────────────────────────

function pt(t: string, v: number, run?: string): RawSeriesPoint {
  return run ? { t, v, run } : { t, v };
}

// Model dimension catalog shared by every brand (canonical labels). gptweb is
// included so its member renders as "ChatGPT (Web)" while staying out of overall.
const MODEL_LABELS: Record<string, string> = {
  openai: "ChatGPT",
  claude: "Claude",
  gemini: "Gemini",
  perplexity: "Perplexity",
  gptweb: "ChatGPT (Web)",
};

// ── Iterate ──────────────────────────────────────────────────────────────────
// Baseline imported Apr 27; Day 11 re-measured Jun 22. AI Index (monitoring) runs
// more often than the Deep Signal re-measurement, which is expected and honest.

function iterate(): RawEnvelope {
  const B = "2026-04-27T15:00:00Z"; // baseline (Deep Signal)
  const D = "2026-06-22T15:00:00Z"; // Day 11 (Deep Signal)
  // Intermediate variable-run timestamps so the sparklines have shape.
  const vb = "2026-05-11T15:00:00Z";
  const vc = "2026-05-25T15:00:00Z";
  const vd = "2026-06-08T15:00:00Z";
  // Monitoring AI Index points (denser cadence than Deep Signal).
  const m1 = "2026-04-28T11:00:00Z";
  const m2 = "2026-05-20T11:00:00Z";
  const m3 = "2026-06-10T11:00:00Z";
  const m4 = "2026-06-23T11:00:00Z";

  return {
    brand: "Iterate",
    customer_id: 8,
    generated_at: "2026-06-29T12:00:00Z",
    layers: [
      {
        key: "ai_index",
        label: "AI Index",
        group: "headline",
        source: "monitoring",
        unit: "score",
        dimensions: ["overall", "model"],
        series: {
          overall: [pt(m1, 51, "bm-1"), pt(m2, 53, "bm-2"), pt(m3, 56, "bm-3"), pt(m4, 58, "bm-4")],
          model: {
            // Divergent, crossing trajectories (not lockstep): ChatGPT climbs
            // hard from low, Claude sits high and nearly flat, Gemini dips then
            // recovers, Perplexity wobbles up.
            openai: [pt(m1, 42, "bm-1"), pt(m2, 46, "bm-2"), pt(m3, 50, "bm-3"), pt(m4, 55, "bm-4")],
            claude: [pt(m1, 60, "bm-1"), pt(m2, 61, "bm-2"), pt(m3, 60, "bm-3"), pt(m4, 62, "bm-4")],
            gemini: [pt(m1, 48, "bm-1"), pt(m2, 47, "bm-2"), pt(m3, 53, "bm-3"), pt(m4, 56, "bm-4")],
            perplexity: [pt(m1, 51, "bm-1"), pt(m2, 55, "bm-2"), pt(m3, 54, "bm-3"), pt(m4, 57, "bm-4")],
          },
        },
      },
      {
        key: "funnel.mention",
        label: "Mention Rate",
        group: "funnel",
        source: "deep_signal",
        unit: "rate",
        dimensions: ["overall", "model"],
        series: {
          overall: [pt(B, 0.62, "ds-10"), pt(D, 0.71, "ds-14")],
          model: {
            // Different slopes and directions: ChatGPT jumps, Claude already
            // high and flat, Gemini slips slightly, Perplexity climbs.
            openai: [pt(B, 0.5, "ds-10"), pt(D, 0.72, "ds-14")],
            claude: [pt(B, 0.68, "ds-10"), pt(D, 0.7, "ds-14")],
            gemini: [pt(B, 0.6, "ds-10"), pt(D, 0.58, "ds-14")],
            perplexity: [pt(B, 0.64, "ds-10"), pt(D, 0.75, "ds-14")],
            // gptweb is a measurement-only lane: present as its own member, but
            // deliberately NOT folded into `overall`.
            gptweb: [pt(B, 0.55, "ds-10"), pt(D, 0.66, "ds-14")],
          },
        },
      },
      {
        key: "funnel.top5",
        label: "Best-in-Category (Top 5)",
        group: "funnel",
        source: "deep_signal",
        unit: "rate",
        dimensions: ["overall", "model"],
        series: {
          overall: [pt(B, 0.41, "ds-10"), pt(D, 0.52, "ds-14")],
          model: {
            openai: [pt(B, 0.3, "ds-10"), pt(D, 0.55, "ds-14")],
            claude: [pt(B, 0.5, "ds-10"), pt(D, 0.51, "ds-14")],
            gemini: [pt(B, 0.42, "ds-10"), pt(D, 0.44, "ds-14")],
            perplexity: [pt(B, 0.4, "ds-10"), pt(D, 0.58, "ds-14")],
          },
        },
      },
      {
        key: "funnel.number_one",
        label: "#1 Recommendation",
        group: "funnel",
        source: "deep_signal",
        unit: "rate",
        dimensions: ["overall", "model"],
        series: {
          overall: [pt(B, 0.08, "ds-10"), pt(D, 0.15, "ds-14")],
          model: {
            openai: [pt(B, 0.05, "ds-10"), pt(D, 0.17, "ds-14")],
            claude: [pt(B, 0.13, "ds-10"), pt(D, 0.14, "ds-14")],
            gemini: [pt(B, 0.06, "ds-10"), pt(D, 0.1, "ds-14")],
            perplexity: [pt(B, 0.08, "ds-10"), pt(D, 0.18, "ds-14")],
          },
        },
      },
      {
        key: "research_variable_score",
        label: "Research Variable Score",
        group: "variables",
        source: "deep_signal",
        unit: "rate",
        dimensions: ["variable"],
        series: {
          variable: {
            rv_pricing:     [pt(B, 0.41, "ds-10"), pt(vb, 0.44, "ds-11"), pt(vc, 0.48, "ds-12"), pt(vd, 0.52, "ds-13"), pt(D, 0.55, "ds-14")],
            rv_support:     [pt(B, 0.52, "ds-10"), pt(vb, 0.55, "ds-11"), pt(vc, 0.57, "ds-12"), pt(vd, 0.59, "ds-13"), pt(D, 0.6, "ds-14")],
            rv_roi:         [pt(B, 0.3, "ds-10"),  pt(vb, 0.34, "ds-11"), pt(vc, 0.4, "ds-12"),  pt(vd, 0.45, "ds-13"), pt(D, 0.52, "ds-14")],
            rv_onboarding:  [pt(B, 0.6, "ds-10"),  pt(vb, 0.58, "ds-11"), pt(vc, 0.55, "ds-12"), pt(vd, 0.53, "ds-13"), pt(D, 0.5, "ds-14")],
            rv_reliability: [pt(B, 0.48, "ds-10"), pt(vb, 0.5, "ds-11"),  pt(vc, 0.49, "ds-12"), pt(vd, 0.51, "ds-13"), pt(D, 0.5, "ds-14")],
            rv_ecosystem:   [pt(B, 0.38, "ds-10"), pt(vb, 0.37, "ds-11"), pt(vc, 0.36, "ds-12"), pt(vd, 0.35, "ds-13"), pt(D, 0.34, "ds-14")],
            // Appears only in the later runs (alignment demo).
            rv_integration: [pt(vd, 0.44, "ds-13"), pt(D, 0.47, "ds-14")],
          },
        },
      },
      // Persona + competitive layers intentionally omitted: no data for these yet.
    ],
    dimension_catalog: {
      model: MODEL_LABELS,
      variable: {
        rv_pricing: "Pricing transparency",
        rv_support: "Support quality",
        rv_integration: "Integration depth",
        rv_roi: "ROI clarity",
        rv_onboarding: "Onboarding speed",
        rv_reliability: "Reliability",
        rv_ecosystem: "Ecosystem fit",
      },
    },
  };
}

// ── Sunbit ───────────────────────────────────────────────────────────────────
// AI Index trends over monitoring runs; Deep Signal has a SINGLE baseline run, so
// funnel/variable lenses render a "baseline only" state. Competitive comes back
// empty (names captured, no rates measured) and must degrade out.

function sunbit(): RawEnvelope {
  const BASE = "2026-06-20T16:00:00Z"; // single Deep Signal run
  const s1 = "2026-06-08T11:00:00Z";
  const s2 = "2026-06-18T11:00:00Z";
  const s3 = "2026-06-27T11:00:00Z";

  return {
    brand: "Sunbit",
    customer_id: 3,
    generated_at: "2026-06-29T12:00:00Z",
    layers: [
      {
        key: "ai_index",
        label: "AI Index",
        group: "headline",
        source: "monitoring",
        unit: "score",
        dimensions: ["overall", "model"],
        series: {
          overall: [pt(s1, 60, "bm-91"), pt(s2, 62, "bm-92"), pt(s3, 63, "bm-93")],
          model: {
            openai: [pt(s1, 55, "bm-91"), pt(s2, 60, "bm-92"), pt(s3, 61, "bm-93")],
            claude: [pt(s1, 66, "bm-91"), pt(s2, 65, "bm-92"), pt(s3, 66, "bm-93")],
            gemini: [pt(s1, 58, "bm-91"), pt(s2, 57, "bm-92"), pt(s3, 61, "bm-93")],
            perplexity: [pt(s1, 61, "bm-91"), pt(s2, 64, "bm-92"), pt(s3, 63, "bm-93")],
          },
        },
      },
      {
        key: "funnel.mention",
        label: "Mention Rate",
        group: "funnel",
        source: "deep_signal",
        unit: "rate",
        dimensions: ["overall", "model"],
        series: {
          overall: [pt(BASE, 0.55, "ds-7")],
          model: {
            openai: [pt(BASE, 0.51, "ds-7")],
            claude: [pt(BASE, 0.6, "ds-7")],
            gemini: [pt(BASE, 0.53, "ds-7")],
            perplexity: [pt(BASE, 0.56, "ds-7")],
          },
        },
      },
      {
        key: "funnel.top5",
        label: "Best-in-Category (Top 5)",
        group: "funnel",
        source: "deep_signal",
        unit: "rate",
        dimensions: ["overall", "model"],
        series: {
          overall: [pt(BASE, 0.34, "ds-7")],
          model: {
            openai: [pt(BASE, 0.3, "ds-7")],
            claude: [pt(BASE, 0.39, "ds-7")],
            gemini: [pt(BASE, 0.32, "ds-7")],
            perplexity: [pt(BASE, 0.35, "ds-7")],
          },
        },
      },
      {
        key: "funnel.number_one",
        label: "#1 Recommendation",
        group: "funnel",
        source: "deep_signal",
        unit: "rate",
        dimensions: ["overall", "model"],
        series: {
          overall: [pt(BASE, 0.11, "ds-7")],
          model: {
            openai: [pt(BASE, 0.09, "ds-7")],
            claude: [pt(BASE, 0.14, "ds-7")],
            gemini: [pt(BASE, 0.1, "ds-7")],
            perplexity: [pt(BASE, 0.12, "ds-7")],
          },
        },
      },
      {
        key: "research_variable_score",
        label: "Research Variable Score",
        group: "variables",
        source: "deep_signal",
        unit: "rate",
        dimensions: ["variable"],
        series: {
          variable: {
            rv_approval: [pt(BASE, 0.48, "ds-7")],
            rv_merchant: [pt(BASE, 0.4, "ds-7")],
          },
        },
      },
      {
        // Competitive layer present in the contract but EMPTY (rates unmeasured).
        // The validator drops empty member arrays, so this layer must vanish from
        // the lens list rather than render a blank chart.
        key: "competitive_position",
        label: "Competitive Position (Mention)",
        group: "competitive",
        source: "deep_signal",
        unit: "rate",
        dimensions: ["competitor"],
        series: {
          competitor: {
            Affirm: [],
            Klarna: [],
          },
        },
      },
    ],
    dimension_catalog: {
      model: MODEL_LABELS,
      variable: { rv_approval: "Approval experience", rv_merchant: "Merchant fit" },
      competitor: { Affirm: "Affirm", Klarna: "Klarna" },
    },
  };
}

// ── Northwind ────────────────────────────────────────────────────────────────
// A generic, fully-populated multi-run brand (4 Deep Signal runs) with a complete
// competitive layer — demonstrates the system on a richer dataset and that an
// arbitrary brand needs no per-brand code.

function northwind(): RawEnvelope {
  const r = ["2026-03-15T15:00:00Z", "2026-04-19T15:00:00Z", "2026-05-24T15:00:00Z", "2026-06-21T15:00:00Z"];
  const bm = ["2026-03-16T11:00:00Z", "2026-04-20T11:00:00Z", "2026-05-25T11:00:00Z", "2026-06-22T11:00:00Z"];
  const ramp = (base: number, step: number) => r.map((t, i) => pt(t, base + step * i, `ds-${100 + i}`));
  const rampM = (base: number, step: number) => bm.map((t, i) => pt(t, base + step * i, `bm-${200 + i}`));

  return {
    brand: "Northwind",
    customer_id: 21,
    generated_at: "2026-06-29T12:00:00Z",
    layers: [
      {
        key: "ai_index",
        label: "AI Index",
        group: "headline",
        source: "monitoring",
        unit: "score",
        dimensions: ["overall", "model"],
        series: {
          overall: rampM(48, 3),
          model: {
            openai: rampM(44, 3),
            claude: rampM(54, 2.5),
            gemini: rampM(46, 3),
            perplexity: rampM(49, 2.8),
          },
        },
      },
      {
        key: "funnel.mention",
        label: "Mention Rate",
        group: "funnel",
        source: "deep_signal",
        unit: "rate",
        dimensions: ["overall", "model"],
        series: {
          overall: ramp(0.5, 0.06),
          model: { openai: ramp(0.46, 0.06), claude: ramp(0.55, 0.05), gemini: ramp(0.48, 0.06), perplexity: ramp(0.52, 0.06) },
        },
      },
      {
        key: "funnel.primary_rec",
        label: "Primary Recommendation",
        group: "funnel",
        source: "deep_signal",
        unit: "rate",
        dimensions: ["overall", "model"],
        series: {
          overall: ramp(0.12, 0.05),
          model: { openai: ramp(0.1, 0.05), claude: ramp(0.16, 0.05), gemini: ramp(0.11, 0.05), perplexity: ramp(0.13, 0.05) },
        },
      },
      {
        key: "research_variable_score",
        label: "Research Variable Score",
        group: "variables",
        source: "deep_signal",
        unit: "rate",
        dimensions: ["variable"],
        series: {
          variable: {
            rv_quality: ramp(0.4, 0.05),
            rv_price: ramp(0.35, 0.06),
            rv_service: ramp(0.5, 0.04),
          },
        },
      },
      // Competitive layer omitted: no data for it yet.
    ],
    dimension_catalog: {
      model: MODEL_LABELS,
      variable: { rv_quality: "Product quality", rv_price: "Price competitiveness", rv_service: "Service reliability" },
    },
  };
}

// ── Registry of fixture brands ───────────────────────────────────────────────

// ── Beacon ───────────────────────────────────────────────────────────────────
// Monitoring-only brand: AI Index exists, but NO Deep Signal run, so the funnel
// topline cards render greyed with a "Run Deep Signal" lock.

function beacon(): RawEnvelope {
  const m1 = "2026-05-10T11:00:00Z";
  const m2 = "2026-05-24T11:00:00Z";
  const m3 = "2026-06-07T11:00:00Z";
  const m4 = "2026-06-21T11:00:00Z";
  return {
    brand: "Beacon",
    customer_id: 42,
    generated_at: "2026-06-29T12:00:00Z",
    layers: [
      {
        key: "ai_index",
        label: "AI Index",
        group: "headline",
        source: "monitoring",
        unit: "score",
        dimensions: ["overall", "model"],
        series: {
          overall: [pt(m1, 49, "bm-1"), pt(m2, 52, "bm-2"), pt(m3, 54, "bm-3"), pt(m4, 57, "bm-4")],
          model: {
            openai: [pt(m1, 46, "bm-1"), pt(m2, 50, "bm-2"), pt(m3, 52, "bm-3"), pt(m4, 56, "bm-4")],
            claude: [pt(m1, 58, "bm-1"), pt(m2, 57, "bm-2"), pt(m3, 59, "bm-3"), pt(m4, 60, "bm-4")],
            gemini: [pt(m1, 47, "bm-1"), pt(m2, 49, "bm-2"), pt(m3, 53, "bm-3"), pt(m4, 55, "bm-4")],
            perplexity: [pt(m1, 50, "bm-1"), pt(m2, 53, "bm-2"), pt(m3, 54, "bm-3"), pt(m4, 57, "bm-4")],
          },
        },
      },
      // No Deep Signal layers on purpose.
    ],
    dimension_catalog: { model: MODEL_LABELS },
  };
}

const BUILDERS: Record<string, () => RawEnvelope> = {
  iterate: iterate,
  sunbit: sunbit,
  northwind: northwind,
  beacon: beacon,
};

export function listFixtureBrands(): string[] {
  return ["Iterate", "Sunbit", "Northwind", "Beacon"];
}

/**
 * Resolve a brand name (case/substring-insensitive, mirroring the Deep Signal
 * page's partial match) to its synthetic raw envelope. Returns null if there is
 * no fixture for the brand, so the caller can fall back to an empty state.
 */
export function getFixtureEnvelope(brand: string | null | undefined): RawEnvelope | null {
  const q = (brand || "").trim().toLowerCase();
  if (!q) return null;
  for (const [key, build] of Object.entries(BUILDERS)) {
    if (q.includes(key) || key.includes(q)) return build();
  }
  return null;
}
